import {
	FormProvider,
	getFormProps,
	getInputProps,
	getTextareaProps,
	useForm,
} from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { type Recibo, type Constancia } from '@prisma/client'
import { type ActionFunctionArgs } from '@remix-run/node'
import { Form, json, useActionData } from '@remix-run/react'
import { z } from 'zod'
import {
	Field,
	TextareaField,
	ErrorList,
	UsoDeCFDIField,
	FormaDePagoField,
} from '#app/components/forms.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { requireUserId } from '#app/utils/auth.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import { redirectWithToast } from '#app/utils/toast.server.ts'
import { runFaguadalajara } from '#app/vendors/faguadalajara'
import { FaguadalajaraFormSchema } from '../../utils/schemas'

export async function action({ request }: ActionFunctionArgs) {
	const userId = await requireUserId(request)
	const formData = await request.formData()
	const owner = await prisma.user.findFirst({
		select: {
			id: true,
			name: true,
			username: true,
			image: { select: { id: true } },
			notes: { select: { id: true, title: true } },
		},
		where: { id: userId },
	})

	const submission = await parseWithZod(formData, {
		schema: FaguadalajaraFormSchema.superRefine(async (data, ctx) => {
			const {
				folio_factura: folioFactura,
				fecha_de_compra: fechaCompra,
				caja,
				num_ticket: numTicket,
				uso_de_cfdi,
			} = data

			if (!folioFactura) {
				ctx.addIssue({
					path: ['folio_factura'],
					code: z.ZodIssueCode.custom,
					message: 'El folio factura es requerido',
				})
			}
			if (!caja) {
				ctx.addIssue({
					path: ['caja'],
					code: z.ZodIssueCode.custom,
					message: 'El No. de caja es requerido',
				})
			}
			if (!fechaCompra) {
				ctx.addIssue({
					path: ['fecha_de_compra'],
					code: z.ZodIssueCode.custom,
					message: 'La fecha de compra es requerida',
				})
			}
			if (!numTicket) {
				ctx.addIssue({
					path: ['num_ticket'],
					code: z.ZodIssueCode.custom,
					message: 'El No. de ticket es requerido',
				})
			}
			if (!uso_de_cfdi) {
				ctx.addIssue({
					path: ['uso_de_cfdi'],
					code: z.ZodIssueCode.custom,
					message: 'Uso de CFDI es requerido',
				})
			}
		}),
		async: true,
	})

	if (submission.status !== 'success') {
		return json(
			{ result: submission.reply() },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	const {
		caja,
		folio_factura,
		num_ticket,
		fecha_de_compra,
		uso_de_cfdi,
		forma_de_pago,
		monto_total,
		observaciones,
	} = submission.value

	try {
		let reciboData = {} as (Recibo & { constancia: Constancia }) | null
		reciboData = await prisma.recibo.create({
			data: {
				vendor: 'faguadalajara',
				monto_total: monto_total,
				fecha_de_compra: fecha_de_compra,
				observaciones,
				uso_de_cfdi: uso_de_cfdi,
				forma_de_pago: forma_de_pago,
				vendorData: JSON.stringify({
					caja: caja,
					num_ticket: num_ticket,
					folio_factura,
				}),
				constancia: {
					connect: {
						userId: owner?.id,
					},
				},
			},
			include: { constancia: true },
		})

		await runFaguadalajara({
			recibo: reciboData,
		})
	} catch (error) {
		json({ result: submission.reply() }, { status: 500 })
	}

	return redirectWithToast(`/recibos`, {
		type: 'success',
		title: 'Success',
		description: 'Has creado tu nuevo recibo.',
	})
}

export default function Test() {
	const actionData = useActionData<typeof action>()
	const isPending = useIsPending()

	const [form, fields] = useForm({
		id: 'faguadalajara-form',
		lastResult: actionData?.result,
		constraint: getZodConstraint(FaguadalajaraFormSchema),
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: FaguadalajaraFormSchema })
		},
	})
	return (
		<FormProvider context={form.context}>
			<Form
				method="POST"
				className="flex h-full w-full flex-col gap-y-4 overflow-y-auto overflow-x-hidden px-10 pb-4 pt-12"
				{...getFormProps(form)}
				encType="multipart/form-data"
			>
				{/*
    This hidden submit button is here to ensure that when the user hits
    "enter" on an input field, the primary form function is submitted
    rather than the first button in the form (which is delete/add image).
*/}
				<button type="submit" className="hidden" />
				<div className="flex flex-col gap-1">
					<Field
						labelProps={{ children: 'Folio Factura' }}
						inputProps={{
							...getInputProps(fields.folio_factura, { type: 'text' }),
						}}
						errors={fields.folio_factura.errors}
					/>
					<FormaDePagoField field={fields.forma_de_pago} />
					<UsoDeCFDIField field={fields.uso_de_cfdi} />
					<Field
						labelProps={{ children: 'Caja' }}
						inputProps={{
							...getInputProps(fields.caja, {
								type: 'number',
							}),
						}}
						errors={fields.caja.errors}
					/>
					<Field
						labelProps={{ children: 'No. de ticket' }}
						inputProps={{
							...getInputProps(fields.num_ticket, {
								type: 'number',
							}),
						}}
						errors={fields.num_ticket.errors}
					/>
					<Field
						labelProps={{ children: 'Fecha de compra' }}
						inputProps={{
							...getInputProps(fields.fecha_de_compra, {
								type: 'date',
							}),
						}}
						errors={fields.fecha_de_compra.errors}
					/>

					<TextareaField
						labelProps={{ children: 'Observaciones' }}
						textareaProps={{
							...getTextareaProps(fields.observaciones),
						}}
						errors={fields.observaciones.errors}
					/>
				</div>

				<ErrorList id={form.errorId} errors={form.errors} />
			</Form>
			<div className="bottom-0 left-3 right-3 flex w-full items-center justify-end gap-2 rounded-lg bg-muted/80 p-4 pl-5 shadow-xl shadow-accent backdrop-blur-sm md:gap-4 md:pl-7">
				<StatusButton
					form={form.id}
					type="submit"
					disabled={isPending}
					status={isPending ? 'pending' : 'idle'}
				>
					Enviar
				</StatusButton>
			</div>
		</FormProvider>
	)
}
